import {
  closestCorners,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  boardFilters,
  categoryOfStatus,
  type KanbanPeriod,
  PIPELINE_GENERAL,
  type Pipeline,
  type TaskStatus,
  type TaskSummary,
} from '@pm/schemas';
import {
  Avatar,
  Button,
  Checkbox,
  cn,
  Counter,
  Dialog,
  DialogContent,
  DialogFooter,
  ErrorState,
  Label,
  PageHeader,
  PriorityBadge,
  Segmented,
  Select,
  Skeleton,
  Tag,
  Textarea,
  Tooltip,
} from '@pm/ui';
import { Kanban, List, ListTree, Lock, Plus, Settings2 } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { api, ApiRequestError } from '@/lib/api';
import { useAuth, useMe } from '@/lib/auth';
import { fmtDue, STATUS_LABEL } from '@/lib/format';
import { moveToStage, usePipelines } from '@/lib/pipelines';
import { useTaskMutation, useTasks, useWorkflowTemplates, useWorkflowVersion } from '@/lib/queries';
import { useToast } from '@/lib/toast';
import { useUpdateViewPrefs, useViewPrefs } from '@/lib/view-prefs';
import { TaskTitle } from '@/features/tasks/task-bits';
import { TaskCreateDialog } from '@/features/tasks/TaskCreateDialog';
import { ACTIVE_STATUSES, filterOpenTasks, PERIOD_OPTIONS, periodBounds } from './board-filters';

/** Códigos em que a API aceita a movimentação mediante override com justificativa (RULE-018). */
const OVERRIDE_CODES = new Set([
  'WORKFLOW_STAGE_LOCKED',
  'WORKFLOW_OVERRIDE_JUSTIFICATION_REQUIRED',
]);

/** status: quadro Geral; workflow: etapas de workflow; manual: colunas do pipeline. */
type BoardMode = 'status' | 'workflow' | 'manual';

interface Column {
  id: string;
  label: string;
  color: string;
  /** Só via ação de domínio (ex.: aprovação). */
  locked?: boolean;
  /** Etapa sem entrada manual: só por auto-avanço ou override. */
  lockedEntry?: boolean;
  /** Categoria da coluna (pipeline manual), para o cartão sinalizar status diferente. */
  category?: TaskStatus;
}

const STATUS_COLUMNS: Column[] = (
  [
    ['NOT_STARTED', 'var(--status-not-started)'],
    ['IN_PROGRESS', 'var(--status-in-progress)'],
    ['WAITING', 'var(--status-waiting)'],
    ['BLOCKED', 'var(--status-blocked)'],
    ['PENDING_APPROVAL', 'var(--status-pending-approval)'],
    ['COMPLETED', 'var(--status-completed)'],
  ] as [TaskStatus, string][]
).map(([status, color]) => ({
  id: status,
  label: STATUS_LABEL[status] ?? status,
  color,
  locked: status === 'PENDING_APPROVAL',
}));

/** Cada movimento de status vira a ação de domínio correspondente; a API decide (RULE-056). */
function statusAction(target: TaskStatus): { path: string; body?: Record<string, unknown> } | null {
  switch (target) {
    case 'IN_PROGRESS':
      return { path: 'start' };
    case 'COMPLETED':
      return { path: 'complete' };
    case 'PENDING_APPROVAL':
      return { path: 'request-approval' };
    case 'NOT_STARTED':
    case 'WAITING':
    case 'BLOCKED':
      return { path: 'set-status', body: { status: target } };
    default:
      return null;
  }
}

const stageColumnId = (sortOrder: number) => `stage:${sortOrder}`;
const sortOrderOf = (columnId: string) => Number(columnId.replace('stage:', ''));

/** Coluna de um cartão conforme o tipo do quadro. */
function columnOf(task: TaskSummary, mode: BoardMode): string | undefined {
  if (mode === 'workflow') return stageColumnId(task.workflow?.stage?.sort_order ?? 0);
  if (mode === 'manual') return task.pipeline?.stage?.id;
  return task.status;
}

/**
 * Pipelines (SPEC §44 revisto): quadro Geral por status (demandas sem pipeline), pipelines com
 * colunas próprias e pipelines de workflow. Filtros salvos por quadro; a API valida cada movimento.
 */
export function PipelinesPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useAuth();
  const me = useMe();
  const { prefs: viewPrefs, ready: prefsReady } = useViewPrefs();
  const updatePrefs = useUpdateViewPrefs();
  const pipelinesQuery = usePipelines();
  const pipelines = useMemo(() => pipelinesQuery.data ?? [], [pipelinesQuery.data]);
  const canManage = can('pipelines.manage');

  // Quadro salvo; se o pipeline sumiu (arquivado, saí dos membros), volta ao Geral.
  const saved = viewPrefs.pipelines.selected;
  const pipeline: Pipeline | undefined =
    saved === PIPELINE_GENERAL ? undefined : pipelines.find((p) => p.id === saved);
  const board = pipeline ? pipeline.id : PIPELINE_GENERAL;
  const filters = boardFilters(viewPrefs, board);
  const setFilters = (patch: Partial<typeof filters>) =>
    updatePrefs({ pipelines: { filters: { [board]: patch } } });
  const selectBoard = (key: string) => updatePrefs({ pipelines: { selected: key } });

  const templatesQuery = useWorkflowTemplates();
  const templateId = pipeline?.source === 'WORKFLOW' ? pipeline.workflow_template?.id : undefined;
  const template = (templatesQuery.data ?? []).find((t) => t.id === templateId && t.published);
  const { data: version } = useWorkflowVersion(template?.published?.id);
  const mode: BoardMode = !pipeline
    ? 'status'
    : pipeline.source === 'WORKFLOW'
      ? 'workflow'
      : 'manual';

  const [createOpen, setCreateOpen] = useState(false);
  const [active, setActive] = useState<TaskSummary | null>(null);
  const [override, setOverride] = useState<{
    task: TaskSummary;
    toSort: number;
    name: string;
  } | null>(null);
  const [justification, setJustification] = useState('');

  // Limites do período recalculados uma vez por dia (a chave da consulta fica estável).
  const today = format(new Date(), 'yyyy-MM-dd');
  const bounds = useMemo(
    () => periodBounds(filters.period),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.period, today],
  );
  const base = {
    root_only: true,
    ...(mode === 'workflow' ? { workflow_template_id: templateId } : {}),
    ...(mode === 'manual' ? { pipeline_id: pipeline!.id } : {}),
    ...(mode === 'status' ? { pipeline_id: 'none' as const } : {}),
    ...(filters.responsible === 'me' ? { responsible_user_id: me.user.id } : {}),
  };
  // Só consulta quando já se sabe qual quadro abrir (evita buscar o Geral à toa).
  const queryReady =
    prefsReady &&
    (saved === PIPELINE_GENERAL || !pipelinesQuery.isLoading) &&
    (mode !== 'workflow' || !templatesQuery.isLoading);
  const open = useTasks(
    {
      ...base,
      per_page: 200,
      sort: 'priority',
      status: ['NOT_STARTED', ...ACTIVE_STATUSES],
    },
    queryReady,
  );
  const done = useTasks(
    {
      ...base,
      per_page: 100,
      sort: 'completed_at',
      dir: 'desc',
      status: ['COMPLETED'],
      completed_from: bounds.completedFrom.toISOString(),
    },
    queryReady,
  );
  const isLoading = !queryReady || open.isLoading || done.isLoading;
  const isError = open.isError || done.isError;
  const error = open.error ?? done.error;
  const refetch = () => Promise.all([open.refetch(), done.refetch()]);
  const filtered = useMemo(
    () => filterOpenTasks(open.data?.data ?? [], bounds, filters.include_no_due),
    [open.data, bounds, filters.include_no_due],
  );
  const rows = useMemo(
    () => [...filtered.visible, ...(done.data?.data ?? [])],
    [filtered.visible, done.data],
  );

  const columns = useMemo<Column[]>(() => {
    if (mode === 'status') return STATUS_COLUMNS;
    if (mode === 'manual')
      return pipeline!.stages.map((s) => ({
        id: s.id,
        label: s.name,
        color: s.color,
        category: s.category,
      }));
    return [...(version?.stages ?? [])]
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((s) => ({
        id: stageColumnId(s.sort_order),
        label: s.name,
        color: s.color ?? 'var(--text-3)',
        lockedEntry: !s.manual_entry_allowed,
      }));
  }, [mode, pipeline, version]);

  const grouped = useMemo(() => {
    const map = new Map<string, TaskSummary[]>();
    for (const c of columns) map.set(c.id, []);
    for (const t of rows) {
      const col = columnOf(t, mode);
      (col ? map.get(col) : undefined)?.push(t);
    }
    return map;
  }, [rows, columns, mode]);

  const move = useTaskMutation(
    ({
      task,
      column,
      justification: j,
    }: {
      task: TaskSummary;
      column: string;
      justification?: string;
    }) => {
      if (mode === 'workflow')
        return api.post(`/tasks/${task.id}/move-stage`, {
          to_sort_order: sortOrderOf(column),
          justification: j,
        });
      if (mode === 'manual') return moveToStage(task.id, column);
      const action = statusAction(column as TaskStatus);
      return action
        ? api.post(`/tasks/${task.id}/${action.path}`, action.body ?? {})
        : Promise.resolve(undefined);
    },
  );

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  function onDragEnd(e: DragEndEvent) {
    setActive(null);
    const column = e.over?.id as string | undefined;
    const task = rows.find((t) => t.id === e.active.id);
    if (!column || !task) return;
    if (columnOf(task, mode) === column) return;
    const label = columns.find((c) => c.id === column)?.label ?? column;
    move.mutate(
      { task, column },
      {
        onSuccess: () => toast.success(`#${task.number} → ${label}`),
        onError: (err) => {
          if (
            mode === 'workflow' &&
            err instanceof ApiRequestError &&
            OVERRIDE_CODES.has(err.code) &&
            can('tasks.override_workflow')
          ) {
            setOverride({ task, toSort: sortOrderOf(column), name: label });
          } else toast.error(err);
        },
      },
    );
  }

  const subtitle =
    mode === 'status'
      ? 'Geral: demandas sem pipeline, por status. Arraste para mudar o status.'
      : mode === 'workflow'
        ? `Colunas são as etapas do workflow ${pipeline?.workflow_template?.name ?? ''}.`
        : (pipeline?.description ??
          'Arraste entre colunas; mudar de categoria inicia, conclui ou pausa a demanda.');

  return (
    <div className="flex h-[calc(100vh-var(--topbar-h)-2*var(--page-pad))] flex-col gap-gap-lg">
      <PageHeader
        title="Pipelines"
        subtitle={subtitle}
        actions={
          <>
            <Select
              aria-label="Pipeline"
              value={board}
              onChange={(e) => selectBoard(e.target.value)}
              className="w-[240px]"
            >
              <option value={PIPELINE_GENERAL}>Geral (sem pipeline)</option>
              {pipelines.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.source === 'WORKFLOW' ? ' · workflow' : ''}
                </option>
              ))}
            </Select>
            {canManage && pipeline ? (
              <Tooltip content="Configurar colunas e membros">
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label="Configurar pipeline"
                  onClick={() => void navigate(`/pipelines/${pipeline.id}/editar`)}
                >
                  <Settings2 />
                </Button>
              </Tooltip>
            ) : null}
            {canManage ? (
              <Button variant="ghost" onClick={() => void navigate('/pipelines/novo')}>
                <Plus /> Novo pipeline
              </Button>
            ) : null}
            <Segmented
              ariaLabel="Visualização"
              value="kanban"
              onChange={(v) => v === 'list' && navigate('/demandas')}
              options={[
                {
                  value: 'list',
                  label: (
                    <>
                      <List /> Lista
                    </>
                  ),
                },
                {
                  value: 'kanban',
                  label: (
                    <>
                      <Kanban /> Quadro
                    </>
                  ),
                },
              ]}
            />
            {can('tasks.create') ? (
              <Button variant="primary" onClick={() => setCreateOpen(true)}>
                <Plus /> Nova demanda
              </Button>
            ) : null}
          </>
        }
      />

      <KanbanFilterBar
        period={filters.period}
        responsible={filters.responsible}
        includeNoDue={filters.include_no_due}
        dueTo={bounds.dueTo}
        completedFrom={bounds.completedFrom}
        outOfPeriod={isLoading ? 0 : filtered.outOfPeriod}
        noDue={isLoading ? 0 : filtered.noDue}
        onChange={setFilters}
      />

      {isError ? (
        <ErrorState message={error?.message} onRetry={() => void refetch()} />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={(e: DragStartEvent) =>
            setActive(rows.find((t) => t.id === e.active.id) ?? null)
          }
          onDragEnd={onDragEnd}
        >
          <div className="flex min-h-0 flex-1 gap-gap-lg overflow-x-auto pb-2">
            {columns.map((col) => (
              <ColumnView
                key={col.id}
                column={col}
                tasks={grouped.get(col.id) ?? []}
                loading={isLoading || (mode === 'workflow' && !version)}
                dragging={active !== null}
                mode={mode}
              />
            ))}
            {mode === 'workflow' && version && columns.length === 0 ? (
              <p className="text-sm text-text-3">Workflow sem etapas publicadas.</p>
            ) : null}
            {mode === 'workflow' && !templatesQuery.isLoading && !template ? (
              <p className="text-sm text-text-3">
                O workflow deste pipeline não tem versão publicada.
              </p>
            ) : null}
          </div>
          <DragOverlay dropAnimation={null}>
            {active ? <Card task={active} overlay mode={mode} column={undefined} /> : null}
          </DragOverlay>
        </DndContext>
      )}

      <TaskCreateDialog
        key={board}
        open={createOpen}
        onOpenChange={setCreateOpen}
        navigateOnCreate={false}
        defaultWorkflowId={mode === 'workflow' ? templateId : undefined}
        defaultPipelineId={mode === 'manual' ? pipeline!.id : undefined}
      />
      <Dialog open={override !== null} onOpenChange={(o) => !o && setOverride(null)}>
        <DialogContent
          title="Movimentação forçada"
          description={`A etapa ${override?.name ?? ''} não permite esta movimentação. Você pode forçar com justificativa (fica no audit log).`}
          size="sm"
        >
          <Label htmlFor="kb-just">Justificativa</Label>
          <Textarea
            id="kb-just"
            autoFocus
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            className="mt-1.5"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOverride(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              disabled={!justification.trim()}
              loading={move.isPending}
              onClick={() =>
                override &&
                move.mutate(
                  { task: override.task, column: stageColumnId(override.toSort), justification },
                  {
                    onSuccess: () => (
                      toast.success('Movimentação forçada registrada'),
                      setOverride(null),
                      setJustification('')
                    ),
                    onError: (e) => toast.error(e),
                  },
                )
              }
            >
              Forçar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const shortDate = (d: Date) => format(d, 'EEE, dd MMM', { locale: ptBR }).replace('.', '');

/** Prazo, responsável e "sem prazo"; tudo salvo nas preferências da pessoa. */
function KanbanFilterBar({
  period,
  responsible,
  includeNoDue,
  dueTo,
  completedFrom,
  outOfPeriod,
  noDue,
  onChange,
}: {
  period: KanbanPeriod;
  responsible: 'all' | 'me';
  includeNoDue: boolean;
  dueTo: Date | null;
  completedFrom: Date;
  outOfPeriod: number;
  noDue: number;
  onChange: (patch: {
    period?: KanbanPeriod;
    responsible?: 'all' | 'me';
    include_no_due?: boolean;
  }) => void;
}) {
  return (
    <div className="-mt-1 flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          aria-label="Prazo"
          value={period}
          onChange={(e) => onChange({ period: e.target.value as KanbanPeriod })}
          className="w-[220px]"
        >
          {PERIOD_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
        <Segmented
          ariaLabel="Responsável"
          value={responsible}
          onChange={(v) => onChange({ responsible: v })}
          options={[
            { value: 'all', label: 'Todas' },
            { value: 'me', label: 'Minhas' },
          ]}
        />
        <label className="flex cursor-pointer items-center gap-1.5 px-1 text-sm text-text-2">
          <Checkbox
            checked={includeNoDue}
            onCheckedChange={(v) => onChange({ include_no_due: v === true })}
          />
          Incluir sem prazo
        </label>
        {outOfPeriod > 0 || noDue > 0 ? (
          <span className="ml-auto flex items-center gap-1 font-mono text-xs text-text-3">
            ocultas:
            {outOfPeriod > 0 ? (
              <button
                type="button"
                onClick={() => onChange({ period: 'all' })}
                className="rounded-sm px-1 text-text-2 underline-offset-2 hover:text-accent hover:underline"
              >
                {outOfPeriod} fora do prazo
              </button>
            ) : null}
            {noDue > 0 ? (
              <button
                type="button"
                onClick={() => onChange({ include_no_due: true })}
                className="rounded-sm px-1 text-text-2 underline-offset-2 hover:text-accent hover:underline"
              >
                {noDue} sem prazo
              </button>
            ) : null}
          </span>
        ) : null}
      </div>
      <p className="font-mono text-xs text-text-3">
        {dueTo
          ? `Não iniciadas com prazo até ${shortDate(dueTo)}, atrasadas incluídas`
          : 'Não iniciadas com qualquer prazo'}
        {' · em andamento, aguardando e bloqueadas sempre aparecem · concluídas desde '}
        {shortDate(completedFrom)}
      </p>
    </div>
  );
}

function ColumnView({
  column,
  tasks,
  loading,
  dragging,
  mode,
}: {
  column: Column;
  tasks: TaskSummary[];
  loading: boolean;
  dragging: boolean;
  mode: BoardMode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id, disabled: column.locked });
  return (
    <section
      ref={setNodeRef}
      className={cn(
        'scroll-host flex w-[280px] shrink-0 flex-col gap-2 rounded-md bg-bg-elev p-2 transition-[outline-color,opacity] duration-(--dur-fast)',
        isOver && 'outline outline-1 -outline-offset-1 outline-dashed outline-accent-border',
        column.locked && dragging && 'opacity-50',
      )}
    >
      <header className="flex items-center gap-2 px-1.5 py-1 font-medium">
        <span className="size-2 rounded-full" style={{ background: column.color }} />
        <span className="truncate">{column.label}</span>
        <Counter tone="neutral">{tasks.length}</Counter>
        {column.locked || column.lockedEntry ? (
          <Lock
            className="ml-auto size-3.5 text-text-3"
            aria-label={
              column.lockedEntry
                ? 'Entrada manual bloqueada (override de gestor)'
                : 'Só via ação de aprovação'
            }
          />
        ) : null}
      </header>
      <div className="scroll-subtle -mr-1 flex min-h-0 flex-1 flex-col gap-2 pr-1">
        {loading ? (
          <>
            <Skeleton className="h-16 rounded-md" />
            <Skeleton className="h-16 rounded-md" />
          </>
        ) : (
          tasks.map((t) => <DraggableCard key={t.id} task={t} mode={mode} column={column} />)
        )}
        {!loading && tasks.length === 0 ? (
          <p className="px-2 py-4 text-center text-sm text-text-3">Vazio</p>
        ) : null}
      </div>
    </section>
  );
}

function DraggableCard({
  task,
  mode,
  column,
}: {
  task: TaskSummary;
  mode: BoardMode;
  column: Column;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: task.id,
    disabled: task.status === 'CANCELED' || (mode === 'workflow' && task.status === 'COMPLETED'),
  });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes} className={cn(isDragging && 'opacity-30')}>
      <Card task={task} mode={mode} column={column} />
    </div>
  );
}

function Card({
  task,
  overlay,
  mode,
  column,
}: {
  task: TaskSummary;
  overlay?: boolean;
  mode: BoardMode;
  column: Column | undefined;
}) {
  // Workflow: o status não é a coluna; pipeline manual: só quando difere da categoria da coluna
  // (ex.: "aguardando aprovação" na coluna de concluídas).
  const showStatus =
    mode === 'workflow' ||
    (mode === 'manual' &&
      column?.category !== undefined &&
      (task.status === 'PENDING_APPROVAL' || categoryOfStatus(task.status) !== column.category));
  return (
    <article
      className={cn(
        'flex cursor-grab flex-col gap-1.5 rounded-md border border-border bg-surface-1 p-2.5 transition-[border-color,box-shadow] duration-(--dur-fast) hover:border-border-strong',
        overlay && 'rotate-1 cursor-grabbing border-accent-border shadow-glow',
      )}
    >
      <TaskTitle task={task} className="line-clamp-2 whitespace-normal" />
      {task.context ? <p className="text-sm text-text-2">{task.context.name}</p> : null}
      <footer className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-xs text-text-3">
        {task.responsible ? (
          <Avatar name={task.responsible.name} size="xs" />
        ) : (
          <span className="grid size-[18px] place-items-center rounded-full border border-dashed border-warning text-[9px] text-warning">
            ?
          </span>
        )}
        <span className={cn(task.is_overdue && 'text-danger')}>{fmtDue(task.due_at)}</span>
        <PriorityBadge priority={task.priority} />
        {showStatus ? <span className="uppercase">{STATUS_LABEL[task.status]}</span> : null}
        {task.subtasks_total > 0 ? (
          <span className="inline-flex items-center gap-0.5">
            <ListTree className="size-3" /> {task.subtasks_completed}/{task.subtasks_total}
          </span>
        ) : null}
        <span className="ml-auto flex gap-1">
          {task.tags.slice(0, 2).map((t) => (
            <Tag key={t.id} color={t.color ?? undefined}>
              {t.name}
            </Tag>
          ))}
        </span>
      </footer>
    </article>
  );
}
